import { type Registry as Services, service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Component from '@glimmer/component';
import { PrismBadge } from '@mylighthouse/prism-ember';
import type { BadgeType } from '@mylighthouse/prism-ember/types/components/prism/badge';
import dayjs, { type OpUnitType } from 'dayjs';
import type { BeniBreakdown } from 'lumen/models';
import assignTextSuccessCriticalClass from 'lumen/modifiers/assign-text-success-critical-class';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  BENI_COLUMN_DATA_TYPE,
  type Kpi,
  KPI_INDEXES_FOR_KPIS,
} from 'lumen/utils/constants/benchmark-insight/beni-constant-values';
import { BENI_TABLE_COLUMN_FORMATS } from 'lumen/utils/constants/benchmark-insight/beni-table-constant-values';

const RANKING_COLOUR_THRESHOLD_PERCENTAGE = 0.25;

interface HotelBenchmarkInsightTablesCellsCellValueSignature {
  Args: {
    column: {
      comparisonSetType: string;
      params: {
        dateAggregation: OpUnitType | undefined;
        kpi: Kpi;
      };
      valueType: string;
    };
    row: {
      date: string;
      depth: number;
      isParent: boolean;
      isTotalRow: boolean;
      primaryComparisonSetBreakdown: BeniBreakdown;
    };
    value: string;
  };
}

export default class HotelBenchmarkInsightTablesCellsCellValue extends Component<HotelBenchmarkInsightTablesCellsCellValueSignature> {
  @service('benchmark-insight/beni-service')
  declare benchmarkInsightBeniService: Services['benchmark-insight/beni-service'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];

  get cellKpi(): Kpi {
    return this.args.column.params.kpi ?? null;
  }

  get isCurrentDayOrWeek(): boolean {
    const { date } = this.args.row;
    if (date) {
      return dayjs(date).isSame(
        this.hotelAndMarketService.getLocalTime(),
        this.args.column.params.dateAggregation,
      );
    }
    return false;
  }

  get isDiffCell(): boolean {
    return this.args.column.valueType === BENI_COLUMN_DATA_TYPE.DIFFERENCE;
  }

  get isIndexCell(): boolean {
    return this.args.column.valueType === BENI_TABLE_COLUMN_FORMATS.INDEX;
  }

  get isRankCell(): boolean {
    return this.args.column.valueType === BENI_TABLE_COLUMN_FORMATS.RANKING;
  }

  get positiveNegativeTextNeutralValue(): number | undefined {
    if (isEmpty(this.args.value)) {
      return undefined;
    }

    if (this.isDiffCell) {
      return 0;
    }

    if (this.isIndexCell) {
      return 100;
    }

    return 0;
  }

  get positiveNegativeTextValue(): number | null {
    const { primaryComparisonSetBreakdown } = this.args.row;

    if (this.isDiffCell) {
      const rawValue =
        primaryComparisonSetBreakdown?.current.total.difference?.[
          this.cellKpi
        ] ?? null;

      if (isEmpty(rawValue)) {
        return null;
      }

      return rawValue;
    }

    if (this.isIndexCell) {
      const rawValue =
        primaryComparisonSetBreakdown?.current.total.index[
          KPI_INDEXES_FOR_KPIS[this.cellKpi]
        ] ?? null;

      // `isEmpty` already checks that `rawValue` is null, but we need to check it again to satisfy TypeScript.
      // We could write a custom type guard to avoid this, but it's not worth it for this case.
      if (isEmpty(rawValue) || rawValue === null) {
        return null;
      }

      return this.benchmarkInsightBeniService.roundIndex(rawValue);
    }

    return null;
  }

  get rankBadgeType(): BadgeType {
    const { primaryComparisonSetBreakdown } = this.args.row;

    const ownRank =
      primaryComparisonSetBreakdown?.current.total.ranking[this.cellKpi] ??
      null;
    const marketTotal =
      primaryComparisonSetBreakdown?.current.total.ranking.size ?? null;

    let typeToDisplay: BadgeType = 'warning';

    if (!marketTotal || !ownRank) {
      return typeToDisplay;
    }

    const dangerThreshold = Math.round(
      marketTotal - marketTotal * RANKING_COLOUR_THRESHOLD_PERCENTAGE,
    );
    const successThreshold = Math.round(
      marketTotal * RANKING_COLOUR_THRESHOLD_PERCENTAGE,
    );

    if (ownRank >= dangerThreshold) {
      typeToDisplay = 'danger';
    }
    if (ownRank <= successThreshold) {
      typeToDisplay = 'success';
    }

    return typeToDisplay;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get valueOrMissing() {
    return this.args.value ?? this.intl.t('general.missing');
  }

  <template>
    {{#if this.isRankCell}}
      <div class="flex flex-row justify-end">
        <PrismBadge
          @badgeType={{this.rankBadgeType}}
          @label={{this.valueOrMissing}}
        />
      </div>
    {{else}}
      <span
        class={{if this.isCurrentDayOrWeek "text-200--bold"}}
        {{assignTextSuccessCriticalClass
          this.positiveNegativeTextValue
          this.positiveNegativeTextNeutralValue
        }}
        data-test-id="cell-value"
      >
        {{this.valueOrMissing}}
      </span>
    {{/if}}
  </template>
}
